import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API } from "../../../constants/apiPath";

const initialState = {
  isLoading: false,
  page: {},
  pages: [],
  error: null,
};

/* ========================================
   THUNK TYPES
======================================== */

const THUNK_METHODS = {
  GET_CMS: "cms/getCMS",
  GET_ALL_CMS: "cms/getAllCMS",
  CREATE_CMS: "cms/createCMS",
  UPDATE_CMS: "cms/updateCMS",
  DELETE_CMS: "cms/deleteCMS",
};

/* ========================================
   GET CMS BY SLUG
======================================== */

export const getCMS = createAsyncThunk(
  THUNK_METHODS.GET_CMS,
  async (slug, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}${API.CMS.GET}/${slug}`,
        {
          withCredentials: true,
        }
      );

      return data.cms;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch CMS page."
      );
    }
  }
);

/* ========================================
   GET ALL CMS
======================================== */

export const getAllCMS = createAsyncThunk(
  THUNK_METHODS.GET_ALL_CMS,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}${API.CMS.GET_ALL}`,
        {
          withCredentials: true,
        }
      );

      return data.cms;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch CMS pages."
      );
    }
  }
);

/* ========================================
   CREATE CMS
======================================== */

export const createCMS = createAsyncThunk(
  THUNK_METHODS.CREATE_CMS,
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}${API.CMS.CREATE}`,
        payload,
        {
          withCredentials: true,
        }
      );

      return data.cms;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);




/* ========================================
   UPDATE CMS
======================================== */

export const updateCMS = createAsyncThunk(
  THUNK_METHODS.UPDATE_CMS,
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_URL}${API.CMS.UPDATE}/${id}`,
        payload,
        {
          withCredentials: true,
        }
      );

      return data.cms;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update CMS."
      );
    }
  }
);

/* ========================================
   DELETE CMS
======================================== */

export const deleteCMS = createAsyncThunk(
  THUNK_METHODS.DELETE_CMS,
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}${API.CMS.DELETE}/${id}`,
        {
          withCredentials: true,
        }
      );

      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete CMS."
      );
    }
  }
);

/* ========================================
   SLICE
======================================== */

export const cmsSlice = createSlice({
  name: "cms",
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder

      /* GET CMS */

      .addCase(getCMS.pending, (state) => {
        state.isLoading = true;
      })

      .addCase(getCMS.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload;
      })

      .addCase(getCMS.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /* GET ALL CMS */

      .addCase(getAllCMS.pending, (state) => {
        state.isLoading = true;
      })

      .addCase(getAllCMS.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pages = action.payload;
      })

      .addCase(getAllCMS.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /* CREATE */

      .addCase(createCMS.pending, (state) => {
        state.isLoading = true;
      })

      .addCase(createCMS.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload;
      })

      .addCase(createCMS.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /* UPDATE */

      .addCase(updateCMS.pending, (state) => {
        state.isLoading = true;
      })

      .addCase(updateCMS.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload;
      })

      .addCase(updateCMS.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /* DELETE */

      .addCase(deleteCMS.pending, (state) => {
        state.isLoading = true;
      })

      .addCase(deleteCMS.fulfilled, (state, action) => {
        state.isLoading = false;

        state.pages = state.pages.filter(
          (page) => page._id !== action.payload
        );
      })

      .addCase(deleteCMS.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export default cmsSlice.reducer;